import { useEffect, useMemo, useState } from "react";
import { useLocation } from "react-router-dom";
import { getLibrary, listPublicThreads } from "@/apps/rag/api/rag-api.js";
import { mapLibrary, mapThread } from "@/apps/rag/api/mappers.js";
import { useRagNavRefreshKey } from "@/apps/rag/hooks/RagNavRefreshContext.js";
import { useRagAuthReady } from "@/apps/rag/hooks/useRagAuthReady.js";
import type { RagNavConfig } from "@/apps/rag/rag-nav.js";
import {
  buildPublicLibraryNav,
  buildRegulatoryGlobalChatNav,
  buildRegulatoryRootNav,
} from "../regulatory-nav.js";
import { REGULATORY_BASE } from "../paths.js";

function isRegulatoryIndex(pathname: string): boolean {
  return pathname === REGULATORY_BASE || pathname === `${REGULATORY_BASE}/`;
}

export function useRegulatoryNavConfig(): RagNavConfig | null {
  const { pathname } = useLocation();
  const refreshKey = useRagNavRefreshKey();
  const { ready } = useRagAuthReady();
  const [loadedConfig, setLoadedConfig] = useState<RagNavConfig | null>(null);

  const immediateConfig = useMemo(() => {
    if (!pathname.startsWith(REGULATORY_BASE)) {
      return null;
    }
    if (isRegulatoryIndex(pathname)) {
      return buildRegulatoryRootNav();
    }
    return null;
  }, [pathname]);

  useEffect(() => {
    if (!pathname.startsWith(REGULATORY_BASE)) {
      setLoadedConfig(null);
      return;
    }

    if (isRegulatoryIndex(pathname)) {
      setLoadedConfig(null);
      return;
    }

    if (!ready) {
      return;
    }

    const isGlobalChat = pathname.startsWith(`${REGULATORY_BASE}/chat`);
    if (isGlobalChat) {
      let cancelled = false;

      async function loadGlobalChatNav() {
        try {
          const { threads } = await listPublicThreads();
          if (cancelled) return;
          setLoadedConfig(buildRegulatoryGlobalChatNav(threads.map(mapThread)));
        } catch {
          if (!cancelled) {
            setLoadedConfig(buildRegulatoryGlobalChatNav());
          }
        }
      }

      void loadGlobalChatNav();
      return () => {
        cancelled = true;
      };
    }

    const publicLibraryMatch = pathname.match(/^\/regulatory\/libraries\/([^/]+)/);
    if (!publicLibraryMatch) {
      setLoadedConfig(buildRegulatoryRootNav());
      return;
    }

    const libraryId = publicLibraryMatch[1];
    let cancelled = false;

    async function loadPublicLibraryNav() {
      try {
        const { library } = await getLibrary(libraryId);
        if (cancelled) return;
        setLoadedConfig(buildPublicLibraryNav(libraryId, mapLibrary(library).name));
      } catch {
        if (!cancelled) {
          setLoadedConfig(buildPublicLibraryNav(libraryId, "Library"));
        }
      }
    }

    void loadPublicLibraryNav();
    return () => {
      cancelled = true;
    };
  }, [pathname, refreshKey, ready]);

  return immediateConfig ?? loadedConfig;
}
